import React, {useEffect, useRef, useState} from 'react';
import CalHeatmap from 'cal-heatmap';
import {ReactComponent as DropDownIcon} from '../../../../src/assests/svg/drop_down.svg';
import {ReactComponent as ChevLeft} from '../../../../src/assests/svg/icon_chevleft.svg';
import {ReactComponent as ChevRight} from '../../../../src/assests/svg/icon_chevright.svg';
import {getDateReport} from '../../../api/EcaManagementService';
import AurButton from '../aur-button/AurButton';
import {AurDropdownButton} from '../aur-button/AurDropdownButton';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {CircularProgress} from '@mui/material';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {t} from 'i18next';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import {ONLY_DATE_SHOW_FORMAT} from '../../../constants/constants';

interface FamilyDateProps {
  totalMonths: number;
  isOpen: boolean;
  projectId: string | null | undefined;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  enableYearNav?: boolean;
  loadFileId?: number;
}

const FamilyDateMap: React.FC<FamilyDateProps> = ({
  totalMonths,
  isOpen,
  projectId,
  onDateRangeChange,
  enableYearNav,
  loadFileId,
}) => {
  const heatmapRef = useRef<HTMLDivElement | null>(null);
  const calRef = useRef<CalHeatmap | null>(null);
  const [loading, setLoading] = useState(false);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  const [familyDateData, setFamilyDateData] = useState<
    Array<{date: string; value: number}>
  >([]);
  const [dateRange, setDateRange] = useState<{
    startYear: number;
    endYear: number;
  } | null>(null);
  const {trackGAEvent} = useAnalytics();

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const [currentYear, setCurrentYear] = useState<number | null>(null);
  const [years, setYears] = useState<number[]>([]);
  let tooltipEl: HTMLDivElement | null = null;

  // Fetch Data for the Selected Year
  const fetchData = async (selectedYear: number) => {
    if (!selectedYear) return;
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      let paramString = `clientId=${clientId}&projectId=${projectId}`;
      if (loadFileId) {
        paramString += `&loadFilesId=${loadFileId}`;
      }
      const response = await getDateReport(paramString);
      const res = response.data;
      const resData = res?.data || [];

      const years: number[] = resData.map((item: any) =>
        new Date(item.date).getFullYear()
      );
      const uniqueYears: number[] = Array.from(new Set<number>(years)).sort(
        (a, b) => a - b
      );
      const startYear: number = uniqueYears[0] ?? null;
      const endYear: number = uniqueYears[uniqueYears.length - 1] ?? null;

      if (onDateRangeChange) {
        onDateRangeChange(`${startYear}-01-01`, `${endYear}-12-31`); // Notify Parent
      }

      setDateRange({startYear, endYear});
      setYears(uniqueYears);

      /* 
        Set only if not already set
        or  
        Set to endYear if currentYear is no longer in available unique years 
      */
      if (currentYear === null || !uniqueYears.includes(currentYear)) {
        setCurrentYear(endYear);
      }

      setFamilyDateData(resData);
    } catch (error) {
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  const trackYearChange = (year: number, type: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: year.toString(),
        region: GA_REGIONS.TOP_NAV,
        type,
        action: t('gaTracking.ecaHomePage.familDateChanged'),
      },
      subPage: {
        subpage_name: t('gaTracking.ecaHomePage.familyDateWidget'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  // When currentYear is set or updated (including initial load), fetch data for that year.
  useEffect(() => {
    if (currentYear !== null) {
      fetchData(currentYear);
    }
  }, [currentYear, isOpen, loadFileId]);

  // Handle Year Selection
  const handleYearSelect = (year: number) => {
    trackYearChange(year, GA_CLICK_TYPES.DROPDOWN);
    setCurrentYear(year);
  };

  const handlePrev = () => {
    const index = currentYear !== null ? years.indexOf(currentYear) : -1;
    if (index > 0) {
      trackYearChange(years[index - 1], GA_CLICK_TYPES.ICON);
      setCurrentYear(years[index - 1]);
    }
  };

  const handleNext = () => {
    const index = currentYear !== null ? years.indexOf(currentYear) : -1;
    if (index >= 0 && index < years.length - 1) {
      trackYearChange(years[index + 1], GA_CLICK_TYPES.ICON);
      setCurrentYear(years[index + 1]);
    }
  };

  // Initialize Calendar on Year Change
  useEffect(() => {
    if (!currentYear || !heatmapRef.current) return;

    if (calRef.current) {
      calRef.current.destroy();
      calRef.current = null;
    }

    const cal = new CalHeatmap();
    calRef.current = cal;

    cal.paint({
      theme: 'light',
      data: {source: familyDateData, x: 'date', y: 'value'},
      date: {start: new Date(`${currentYear}-01-01`)},
      range: totalMonths,
      scale: {
        color: {
          type: 'threshold',
          range: [
            '#ffffff',
            '#dcecff',
            '#a5c1ff',
            '#6e87ff',
            '#4555d6',
            '#2b3585',
            '#1e245c',
          ],
          domain: [1, 100, 500, 1000, 5000, 10000],
        },
      },
      domain: {
        type: 'month',
        label: {position: 'top', height: 50},
        width: 4 * 252,
        height: 640,
      },
      rowLimit: 4,
      subDomain: {
        type: 'xDay',
        radius: 2,
        label: 'DD',
        width: 32,
        height: 32,
      },
      itemSelector: heatmapRef.current,
    });

    cal.on(
      'mouseover',
      (event: MouseEvent, timestamp: number, value: number) => {
        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'custom-tooltip';
          tooltipEl.className = 'tooltip-wrapper';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.background = '#fff';
          tooltipEl.style.color = '#0C1119';
          tooltipEl.style.padding = '12px 16px';
          tooltipEl.style.borderRadius = '8px';
          tooltipEl.style.fontSize = '14px';
          tooltipEl.style.zIndex = '10000';
          tooltipEl.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.2)';
          tooltipEl.style.pointerEvents = 'none';

          const arrowEl = document.createElement('div');
          arrowEl.className = 'tooltip-arrow';
          arrowEl.style.position = 'absolute';
          arrowEl.style.left = '50%';
          arrowEl.style.bottom = '-6px';
          arrowEl.style.transform = 'translateX(-50%)';
          arrowEl.style.width = '0';
          arrowEl.style.height = '0';
          arrowEl.style.borderLeft = '6px solid transparent';
          arrowEl.style.borderRight = '6px solid transparent';
          arrowEl.style.borderTop = '6px solid #fff';
          tooltipEl.appendChild(arrowEl);
          document.body.appendChild(tooltipEl);
        }

        const content = `${getUTCDateTimeForDisplay(timestamp, ONLY_DATE_SHOW_FORMAT)}\n${t('common.documentCount', {count: value || 0})}`;
        tooltipEl.innerHTML = `<div style="text-align:center; margin-bottom:6px;">${content.replace('\n', '<br />')}</div>`;

        const arrow = document.createElement('div');
        arrow.className = 'tooltip-arrow';
        arrow.style.position = 'absolute';
        arrow.style.left = '50%';
        arrow.style.bottom = '-6px';
        arrow.style.transform = 'translateX(-50%)';
        arrow.style.width = '0';
        arrow.style.height = '0';
        arrow.style.borderLeft = '6px solid transparent';
        arrow.style.borderRight = '6px solid transparent';
        arrow.style.borderTop = '6px solid #fff';
        tooltipEl.appendChild(arrow);

        tooltipEl.style.left = `${event.pageX}px`;
        tooltipEl.style.top = `${event.pageY}px`;
        tooltipEl.style.display = 'block';
      }
    );

    // Instead of attaching mouseout to each cell, attach a mouseleave event listener on the heatmap container.
    // This will only fire when the mouse leaves the entire heatmap area.
    if (heatmapRef.current) {
      heatmapRef.current.addEventListener('mouseleave', () => {
        // Optionally use a delay before removal to reduce flicker
        setTimeout(() => {
          if (tooltipEl) {
            tooltipEl.style.display = 'none';
          }
        }, 100);
      });
    }

    return () => {
      if (calRef.current) {
        calRef.current.destroy();
        calRef.current = null;
      }
    };
  }, [currentYear, familyDateData]);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, [loadFileId]);

  return (
    <>
      {enableYearNav && dateRange && (
        <div
          className="year-nav"
          style={{textAlign: 'center', marginBottom: '20px'}}
        >
          <div
            className="aur-alert-carousel-button-icon"
            style={{display: 'flex', justifyContent: 'center', gap: '10px'}}
          >
            <AurButton
              type="continue"
              size="Small"
              onClick={handlePrev}
              ariaLabel={t('common.aurDatePicker.previous')}
            >
              <ChevLeft aria-hidden="true" className="arrow-color" />
            </AurButton>

            <AurDropdownButton
              type="text"
              iconType="Arrow"
              classNames="drop-button"
              buttonText={currentYear?.toString() || ''}
              endIcon={<DropDownIcon aria-hidden="true" />}
              menuItems={years.map((year) => ({
                label: year.toString(),
                onClick: () => handleYearSelect(year),
              }))}
            />

            <AurButton
              type="continue"
              size="Small"
              onClick={handleNext}
              ariaLabel={t('common.aurDatePicker.next')}
            >
              <ChevRight aria-hidden="true" className="arrow-color" />
            </AurButton>
          </div>
        </div>
      )}

      {loading && (
        <div
          style={{
            textAlign: 'center',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </div>
      )}

      {!loading && !isFailedToLoadContent && (
        <section
          className="table-data space-top-30 enable-h-scroll"
          tabIndex={0}
          role="region"
          aria-label={t('ecaHomepageModals.ecaBottom.familyDateHeatmap')}
        >
          <div ref={heatmapRef} id="day" className="heatmap-container"></div>
        </section>
      )}
    </>
  );
};

export default FamilyDateMap;
